/**
 * Domain hook for quick-add templates (`tx_template` table).
 * Same pattern as `use-finance-query.ts`: canonical queryKey + query builder
 * encapsulated so consumers do not call `createPowerSyncQuery`.
 */

import type { Accessor } from "solid-js";
import { keepPreviousData } from "@tanstack/solid-query";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import type { PowerSyncDrizzleDB, TxTemplate } from "@cashsync/powersync";

import { getTxTemplatesQuery, queryKeys } from "../index";

import { createPowerSyncQuery, DEFAULT_THROTTLE_MS } from "./create-power-sync-query";

export interface UseTemplatesQueryOptions {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
  userId: Accessor<string | undefined>;
  staleTime?: number;
  throttleTime?: number;
}

/** User templates ordered by `sort_order`. */
export function useTemplatesQuery(options: UseTemplatesQueryOptions) {
  return createPowerSyncQuery<TxTemplate>({
    queryKey: () => queryKeys.templates.list(options.userId()),
    db: options.db,
    queryFn: () => {
      const db = options.drizzle();
      const uid = options.userId();
      return db && uid ? getTxTemplatesQuery(db, uid) : null;
    },
    enabled: () => Boolean(options.userId()) && Boolean(options.drizzle()),
    placeholderData: keepPreviousData,
    staleTime: options.staleTime,
    throttleTime: options.throttleTime ?? DEFAULT_THROTTLE_MS,
  });
}
